import { useState, useRef, useEffect } from 'react';
import { Stack, Box, Typography, useTheme, alpha } from '@mui/material';
import PulseDot from 'src/components/pulse-dot';
import CiCard from './CiCard';
import { mapLevelToLabel } from '../utils';
import { FlagIcon } from 'src/components/iconify/flagIcon';
import countriesJson from 'src/utils/countries.json';
import { useLocales } from 'src/locales';

export interface CountryWithTrafficShare {
  name: string;
  trafficShare: number;
}

export interface PressurePoolDatum {
  level: string;
  label?: string;
  percentage: number;
  countryCount: number;
  countries?: CountryWithTrafficShare[];
}

interface ByPressureCardProps {
  title: string;
  dateRange: string;
  content?: string;
  pools?: PressurePoolDatum[];
  loading?: boolean;
}

const PRESSURE_LEVEL_COLORS: Record<string, string> = {
  high: '#FF5630',
  active: '#F7CE4A',
  low: '#14C779',
};

const DEFAULT_POOLS: PressurePoolDatum[] = [
  { level: 'high', label: mapLevelToLabel('high'), percentage: 0, countryCount: 0, countries: [] },
  { level: 'active', label: mapLevelToLabel('active'), percentage: 0, countryCount: 0, countries: [] },
  { level: 'low', label: mapLevelToLabel('low'), percentage: 0, countryCount: 0, countries: [] },
];

interface GradientBarProps {
  color: string;
  percentage: number;
  countries?: CountryWithTrafficShare[];
  label: string;
  isHovered: boolean;
  shouldDim: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  dataTestId?: string;
}

function GradientBar({ color, percentage, countries = [], label, isHovered, shouldDim, onMouseEnter, onMouseLeave, dataTestId }: GradientBarProps) {
  const normalized = Math.max(0, Math.min(percentage, 100));
  const width = Math.min((normalized * 3) + 10, 260);
  const theme = useTheme();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isHovered || !tooltipRef.current || !barRef.current) return;

    const updateTooltipPosition = () => {
      if (!tooltipRef.current || !barRef.current) return;
      
      const barRect = barRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let left = barRect.left + barRect.width / 2 - tooltipRect.width / 2;
      let top = barRect.top - tooltipRect.height - 8;

      // Adjust if tooltip goes off screen
      if (left < 8) left = 8;
      if (left + tooltipRect.width > viewportWidth - 8) {
        left = viewportWidth - tooltipRect.width - 8;
      }
      if (top < 8) {
        top = barRect.bottom + 8;
      }

      tooltipRef.current.style.left = `${left}px`;
      tooltipRef.current.style.top = `${top}px`;
    };

    // Use requestAnimationFrame to ensure DOM is rendered
    const rafId = requestAnimationFrame(() => {
      updateTooltipPosition();
    });
    
    window.addEventListener('scroll', updateTooltipPosition, true);
    window.addEventListener('resize', updateTooltipPosition);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updateTooltipPosition, true);
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [isHovered]);

  const isDark = theme.palette.mode === 'dark';
  const tooltipBgColor = isDark 
    ? alpha(theme.palette.grey[800], 0.95)
    : alpha(theme.palette.background.paper, 0.95);
  const textColor = theme.palette.text.primary;
  const textSecondaryColor = theme.palette.text.secondary;
  const borderColor = theme.palette.divider;

  return (
    <>
      <Stack 
        ref={barRef}
        direction='row' 
        display='flex' 
        sx={{ 
          flexGrow: 1,
          position: 'relative',
          opacity: shouldDim ? 0.3 : 1,
          transition: 'opacity 0.2s ease',
          cursor: 'crosshair',
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-testid={dataTestId}
      >
        <Box sx={{ 
          width,
          height: '20px', 
          maxWidth: '100%',
          minWidth: normalized === 0 ? '60px' : 'auto',
          background: 'linear-gradient(to right, rgba(0,0,0,0), #4D5D6B)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          pr: '12px',
          py: '4px',
        }}>
          <Typography fontSize="13px">{normalized}%</Typography>
        </Box>
        <Box sx={{
          width: '2px',
          height: '20px',
          background: color,
          borderRadius: '0px 2px 2px 0px',
        }}/>
      </Stack>
      {isHovered && (
        <Box
          ref={tooltipRef}
          sx={{
            position: 'fixed',
            zIndex: 9900,
            color: textColor,
            backgroundColor: tooltipBgColor,
            border: '1px solid rgba(145, 158, 171, 0.1)',
            borderBottom: '1px solid rgba(145, 158, 171, 0.4)',
            borderColor: borderColor,
            borderRadius: '8px',
            boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
            padding: 0,
            pointerEvents: 'none',
            fontFamily: 'Public Sans, sans-serif',
            minWidth: '200px',
            maxWidth: '300px',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '12px',
                fontFamily: 'Public Sans, sans-serif',
                color: textColor,
              }}
            >
              Countries ({countries.length})
            </Typography>
            <Typography
              sx={{
                fontSize: '12px',
                fontFamily: 'Public Sans, sans-serif',
                color: textColor,
              }}
            >
              Traffic share
            </Typography>
          </Box>
          
          {/* Countries List */}
          {countries.length > 0 ? (
            <Box
              sx={{
                maxHeight: '200px',
                overflowY: 'auto',
              }}
            >
              {countries.map((country, index) => {
                const countryData = countriesJson?.find((c) => c.name === country.name);
                const flagCode = countryData?.code?.toLowerCase();
                
                return (
                  <Box
                    key={`${country.name}-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 16px',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {flagCode ? (
                        <FlagIcon 
                          code={flagCode} 
                          sx={{ width: 20, height: 20, borderRadius: '50%' }}
                        />
                      ) : (
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: isDark ? alpha(theme.palette.grey[700], 0.5) : alpha(theme.palette.grey[300], 0.5),
                      }}
                    />
                      )}
                      <Typography
                        sx={{
                          fontSize: '12px',
                          fontFamily: 'Public Sans, sans-serif',
                          color: textColor,
                        }}
                      >
                        {country.name}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontFamily: 'Public Sans, sans-serif',
                        color: textSecondaryColor,
                        fontWeight: 500,
                      }}
                    >
                      {Math.round(country.trafficShare)}%
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                padding: '24px 16px',
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontFamily: 'Public Sans, sans-serif',
                  color: textSecondaryColor,
                  fontStyle: 'italic',
                }}
              >
                No countries in this pool
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );
}

export default function ByPressureCard({ title, dateRange, pools, loading }: ByPressureCardProps) {
  const { t } = useLocales();
  const [hoveredLevel, setHoveredLevel] = useState<string | null>(null);
  
  const displayPools = (pools && pools.length > 0 ? pools : DEFAULT_POOLS).map((pool) => ({
    ...pool,
    label: pool.label || mapLevelToLabel(pool.level),
    countries: pool.countries || [],
  }));

  // Check if all pools have 0% and no countries
  const hasNoData = displayPools.every(pool => pool.percentage === 0 && (!pool.countries || pool.countries.length === 0));

  const mainContent = () => {
    if (hasNoData) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '120px',
            marginY: '30px',
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
            {t('dashboard.global.no_data_available')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '12px', mt: 1 }}>
            {t('dashboard.global.competitiveIntelligence.no_results_found_description')}
          </Typography>
        </Box>
      );
    }

    return (
      <Stack gap="15px" marginTop="35px">
        {displayPools.map((pool) => {
          const color = PRESSURE_LEVEL_COLORS[pool.level] || '#4D5D6B';
          const isHovered = hoveredLevel === pool.level;
          const hasAnyHover = hoveredLevel !== null;
          const shouldDim = hasAnyHover && !isHovered;
          return (
            <Stack key={pool.level} direction="row" alignItems="center" spacing={1.5}>
              <PulseDot color={color} />
              <Typography 
                fontSize="14px" 
                width="25%" 
                color="#919EAB"
              >
                {pool.label}
              </Typography>
              <GradientBar 
                color={color} 
                percentage={pool.percentage}
                countries={pool.countries}
                label={pool.label || ''}
                isHovered={isHovered}
                shouldDim={shouldDim}
                onMouseEnter={() => setHoveredLevel(pool.level)}
                onMouseLeave={() => setHoveredLevel(null)}
                dataTestId={`pressure-bar-${pool.level}`}
              />
            </Stack>
          );
        })}
      </Stack>
    );
  };

  return (
    <CiCard
      dateRange={dateRange}
      title={title}
      mainContent={mainContent()}
      loading={loading}
    />
  );
}

